/**
 * Joins a walk's viewport tiles into one full-page PNG.
 *
 * The worker gets the page into a state where consecutive screenshots line up
 * (apps/browser/src/stitch.ts); this lays them out. The geometry is the Chrome
 * extension's `captureFullPageStitched`, with its one hard-won rule kept
 * exactly: each tile fills canvas only up to where the *next* tile's measured
 * position begins, never its own full height. Tiles overlap by ~5% on purpose,
 * and advancing by a tile's full height while drawing the overlap-aware region
 * left a band at every boundary that compounded down the page.
 *
 * Generalised to an inner scroller the same way: whatever sits above the
 * scroller (an app shell's header) comes from the first tile, whatever sits
 * below it from the last, and the scrolled content between.
 */

/** Canvas limits, for Chromium and Safari alike: no side over 32,767 device
 *  pixels, and no more than 268,435,456 of them in total. Past either,
 *  `getContext` or `toBlob` fails with nothing more useful than null. */
export const MAX_CANVAS_SIDE = 32_767;
export const MAX_CANVAS_AREA = 268_435_456;

/**
 * The tallest page, in CSS pixels, a stitch of this width can hold.
 *
 * A 1280-wide desktop at 2x is 2,560 device pixels across, so the side limit
 * binds first: 16,383 CSS pixels. The area limit only bites on very wide
 * pages. A page longer than this is cut at the limit rather than failed —
 * the top of a page is the part anybody came to see.
 */
export function maxStitchHeight(pixelWidth: number, dpr: number): number {
  const bySide = Math.floor(MAX_CANVAS_SIDE / dpr);
  const byArea = Math.floor(MAX_CANVAS_AREA / Math.max(1, pixelWidth) / dpr);
  return Math.max(1, Math.min(bySide, byArea));
}

export interface StitchLayout {
  /** The scroller's box in the viewport. 0 / viewportHeight for the window. */
  containerTop: number;
  containerBottom: number;
  viewportHeight: number;
  /** The scroller's scrollHeight. */
  contentHeight: number;
  /** How tall the result is — the page, clamped to what a canvas can hold. */
  finalHeight: number;
}

/** One rectangle copied from a tile onto the canvas, in CSS pixels. */
export interface Slice {
  tile: number;
  srcY: number;
  destY: number;
  height: number;
}

/**
 * Where every tile's pixels go. Pure, so the geometry is tested as data —
 * jsdom has no canvas to draw on.
 *
 * @param tiles each tile's measured scroll position, in the order shot. The
 *   first is 0.
 */
export function planSlices(tiles: number[], layout: StitchLayout): Slice[] {
  const { containerBottom, containerTop, contentHeight, finalHeight } = layout;
  const containerHeight = containerBottom - containerTop;
  const slices: Slice[] = [];
  if (tiles.length === 0 || containerHeight <= 0) return slices;

  const push = (slice: Slice) => {
    const height = Math.min(slice.height, finalHeight - slice.destY);
    if (height > 0) slices.push({ ...slice, height });
  };

  // Above the scroller: the first tile, as it was.
  if (containerTop > 0) {
    push({ destY: 0, height: containerTop, srcY: 0, tile: 0 });
  }

  let covered = tiles[0] ?? 0;
  for (let i = 0; i < tiles.length; i += 1) {
    const at = tiles[i] ?? 0;
    const next = tiles[i + 1];
    const start = Math.max(covered, at);
    // Up to where the next tile begins — or, for the last, as far as this
    // one reaches. Never past what the tile actually shows.
    const end = Math.min(
      next ?? at + containerHeight,
      at + containerHeight,
      contentHeight,
    );
    if (end <= start) continue;
    push({
      destY: containerTop + start,
      height: end - start,
      srcY: containerTop + (start - at),
      tile: i,
    });
    covered = end;
  }

  // Below the scroller: the last tile's footer band.
  const below = layout.viewportHeight - containerBottom;
  if (below > 0) {
    push({
      destY: containerTop + contentHeight,
      height: below,
      srcY: containerBottom,
      tile: tiles.length - 1,
    });
  }

  return slices;
}

/** Whether this browser can stitch at all. False in jsdom, which has no 2D
 *  canvas; the walk then keeps the first screen on its own. */
export function canStitch(): boolean {
  try {
    return Boolean(document.createElement('canvas').getContext('2d'));
  } catch {
    return false;
  }
}

async function decode(base64: string): Promise<ImageBitmap> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return createImageBitmap(new Blob([bytes], { type: 'image/png' }));
}

/**
 * Draw the tiles onto one canvas and encode it.
 *
 * @param tiles base64 PNGs, in the order of `positions`.
 */
export async function stitchTiles({
  background,
  dpr,
  layout,
  positions,
  tiles,
}: {
  background: string;
  dpr: number;
  layout: StitchLayout;
  positions: number[];
  tiles: string[];
}): Promise<Blob> {
  const images = await Promise.all(tiles.map(decode));
  try {
    const first = images[0];
    if (!first) throw new Error('nothing to stitch');

    const toPhys = (value: number) => Math.round(value * dpr);
    const canvas = document.createElement('canvas');
    canvas.width = first.width;
    canvas.height = toPhys(layout.finalHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d canvas to stitch on');
    ctx.imageSmoothingEnabled = false;

    // Any band no tile covers is the page's own background, not transparent.
    ctx.fillStyle = background || '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (const slice of planSlices(positions, layout)) {
      const image = images[slice.tile];
      if (!image) continue;
      const srcY = toPhys(slice.srcY);
      const destY = toPhys(slice.destY);
      // Rounded per edge rather than per height, so adjacent slices meet on
      // the same device pixel instead of leaving a one-pixel seam.
      const height = Math.min(
        toPhys(slice.destY + slice.height) - destY,
        image.height - srcY,
      );
      if (height <= 0) continue;
      ctx.drawImage(
        image,
        0,
        srcY,
        image.width,
        height,
        0,
        destY,
        canvas.width,
        height,
      );
    }

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('the stitched page could not be encoded'));
      }, 'image/png');
    });
  } finally {
    images.forEach((image) => {
      image.close();
    });
  }
}
